import { useEffect, useMemo, useState } from 'react';
import { Seo } from '../components/Seo';

const COLOR_OPTIONS = [
  { value: 'Red', ko: '레드', en: 'Red' },
  { value: 'Purple', ko: '퍼플', en: 'Purple' },
  { value: 'Blue', ko: '블루', en: 'Blue' },
  { value: 'White', ko: '화이트', en: 'White' },
  { value: 'Yellow', ko: '옐로우', en: 'Yellow' },
  { value: 'Black', ko: '블랙', en: 'Black' },
  { value: 'Green', ko: '그린', en: 'Green' },
  { value: 'Pink', ko: '핑크', en: 'Pink' },
  { value: 'Orange', ko: '오렌지', en: 'Orange' },
  { value: 'Brown', ko: '브라운', en: 'Brown' }
] as const;
const MOOD_OPTIONS = [
  { value: 'Bright', ko: '밝은', en: 'Bright' },
  { value: 'Warm', ko: '다정한', en: 'Warm' },
  { value: 'Bouncy', ko: '통통튀는', en: 'Bouncy' },
  { value: 'Rough', ko: '거친', en: 'Rough' },
  { value: 'Sensitive', ko: '예민한', en: 'Sensitive' },
  { value: 'Sharp', ko: '날카로운', en: 'Sharp' },
  { value: 'HotBlooded', ko: '다혈질인', en: 'Hot-Blooded' },
  { value: 'DoubleFaced', ko: '이중인격', en: 'Double-Faced' },
  { value: 'Hidden', ko: '숨겨진', en: 'Hidden' },
  { value: 'Chuunibyou', ko: '중2병', en: 'Chuunibyou' },
  { value: 'Soft', ko: '부드러운', en: 'Soft' },
  { value: 'Romantic', ko: '낭만적', en: 'Romantic' },
  { value: 'Tsundere', ko: '츤데레', en: 'Tsundere' },
  { value: 'Miserly', ko: '구두쇠', en: 'Miserly' },
  { value: 'Burly', ko: '우람한', en: 'Burly' },
  { value: 'Slender', ko: '슬렌더', en: 'Slender' },
  { value: 'Intellectual', ko: '지적인', en: 'Intellectual' }
] as const;
const ABV_OPTIONS = ['Light', 'Medium', 'Strong'] as const;
const FORCE_ORDER_AVAILABLE_FOR_TEST = true;

type AbvOption = (typeof ABV_OPTIONS)[number];
type Lang = 'ko' | 'en';

type SecretOrderPayload = {
  colors: string[];
  moods: string[];
  abv: AbvOption;
  keyword?: string;
  referenceImagePreview?: string;
  createdAtKst: string;
};

type KstInfo = {
  isAvailable: boolean;
  displayNow: string;
  createdAtKst: string;
};

function getKstInfo(now = new Date()): KstInfo {
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);

  const timeParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(now);

  const weekdayText = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    weekday: 'short'
  }).format(now);

  const year = dateParts.find((p) => p.type === 'year')?.value ?? '0000';
  const month = dateParts.find((p) => p.type === 'month')?.value ?? '00';
  const day = dateParts.find((p) => p.type === 'day')?.value ?? '00';
  const hour = Number(timeParts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(timeParts.find((p) => p.type === 'minute')?.value ?? '0');
  const second = Number(timeParts.find((p) => p.type === 'second')?.value ?? '0');
  const hh = String(hour).padStart(2, '0');
  const mm = String(minute).padStart(2, '0');

  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
  };
  const kstDay = weekdayMap[weekdayText] ?? -1;
  const secondsFromMidnight = hour * 3600 + minute * 60 + second;
  const openStart = 17 * 3600;
  const openEnd = 19 * 3600;
  const dayAllowed = kstDay >= 3 && kstDay <= 6;
  const timeAllowed = secondsFromMidnight >= openStart && secondsFromMidnight < openEnd;

  return {
    isAvailable: dayAllowed && timeAllowed,
    displayNow: `${year}-${month}-${day} ${hh}:${mm} KST`,
    createdAtKst: `${year}-${month}-${day} ${hh}:${mm}`
  };
}

const TXT = {
  ko: {
    pageTitle: '타로맨스 | Story Custom',
    pageDesc: '스토리 커스텀 제작 요청',
    heading: 'STORY CUSTOM',
    line1: '색, 분위기, 이미지로 해석하는 스토리 커스텀.',
    line2: '',
    line3: '수·목·금·토 17:00–19:00 한정',
    line4: '가격 : 25,000원',
    line5: '제조 시간 : 1잔 당 약 15분',
    language: 'Language',
    korean: '한국어',
    english: 'English',
    colorLegend: '색상 (필수, 최대 2개)',
    moodLegend: '특징 (필수, 최대 3개)',
    abvLegend: '알콜도수 (필수)',
    keywordLegend: '요청사항 (선택)',
    imageLegend: '참고 이미지 (선택)',
    keywordPlaceholder: '예: neon / petals / midnight',
    imageHelp: '이미지 1개 업로드 (로컬 프리뷰)',
    removeImage: '이미지 제거',
    maxColor: '색은 최대 2개까지 선택 가능합니다.',
    maxMood: '분위기는 최대 3개까지 선택 가능합니다.',
    errColor: '색상은 1~2개 선택해 주세요.',
    errMood: '특징은 1~3개 선택해 주세요.',
    errAbv: '알콜도수를 선택해 주세요.',
    errKeyword: '요청사항은 50자 이내로 입력해 주세요.',
    order: '주문하기',
    summary: '주문 요약',
    statusOn: '주문 가능',
    statusOff: '주문 불가',
    now: '현재',
    closedNote: '현재는 제작 시간이 아닙니다.\n수·목·금·토 17:00–19:00에만 진행됩니다.',
    fieldColor: '색상',
    fieldMood: '특징',
    fieldAbv: '알콜도수',
    fieldKeyword: '요청사항',
    fieldImage: '참고 이미지',
    created: '작성시각(KST)',
    showToStaff: '바텐더에게 이 화면을 보여주세요.',
    close: '닫기',
    noKeyword: '(없음)',
    noImage: '(없음)'
  },
  en: {
    pageTitle: 'Taromance | Story Custom',
    pageDesc: 'Story custom request',
    heading: 'STORY CUSTOM',
    line1: 'A story custom interpreted through color, mood, and image.',
    line2: '',
    line3: 'Available: Wed/Thu/Fri/Sat 17:00–19:00',
    line4: 'Price: KRW 25,000',
    line5: 'Build time: about 15 minutes per glass',
    language: 'Language',
    korean: '한국어',
    english: 'English',
    colorLegend: 'Color (required, up to 2)',
    moodLegend: 'Traits (required, up to 3)',
    abvLegend: 'ABV (required)',
    keywordLegend: 'Request Notes (optional)',
    imageLegend: 'Reference Image (optional)',
    keywordPlaceholder: 'e.g. neon / petals / midnight',
    imageHelp: 'Upload 1 image (local preview only)',
    removeImage: 'Remove image',
    maxColor: 'You can choose up to 2 colors.',
    maxMood: 'You can choose up to 3 moods.',
    errColor: 'Please select 1-2 colors.',
    errMood: 'Please select 1-3 traits.',
    errAbv: 'Please select ABV.',
    errKeyword: 'Request notes must be 50 characters or less.',
    order: 'Order',
    summary: 'Order Summary',
    statusOn: 'Available',
    statusOff: 'Unavailable',
    now: 'Now',
    closedNote: 'Production is closed now.\nAvailable only Wed/Thu/Fri/Sat 17:00–19:00.',
    fieldColor: 'Color',
    fieldMood: 'Traits',
    fieldAbv: 'ABV',
    fieldKeyword: 'Request Notes',
    fieldImage: 'Reference Image',
    created: 'Created (KST)',
    showToStaff: 'Please show this screen to the bartender.',
    close: 'Close',
    noKeyword: '(None)',
    noImage: '(None)'
  }
} as const;

export function SecretOrderPage() {
  const [lang, setLang] = useState<Lang>('ko');
  const [colors, setColors] = useState<string[]>([]);
  const [moods, setMoods] = useState<string[]>([]);
  const [abv, setAbv] = useState<AbvOption | ''>('');
  const [keyword, setKeyword] = useState('');
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [referencePreview, setReferencePreview] = useState<string | null>(null);
  const [hint, setHint] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState<SecretOrderPayload | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const kst = useMemo(() => getKstInfo(new Date(nowTick)), [nowTick]);
  const popupIsAvailable = FORCE_ORDER_AVAILABLE_FOR_TEST ? true : kst.isAvailable;
  const t = TXT[lang];

  function labelByLang(option: { ko: string; en: string }): string {
    return lang === 'ko' ? option.ko : option.en;
  }

  function abvLabel(value: AbvOption): string {
    if (lang === 'ko') {
      if (value === 'Light') return '약하게';
      if (value === 'Medium') return '적당히';
      return '강하게';
    }
    return value;
  }

  function colorLabel(value: string): string {
    const found = COLOR_OPTIONS.find((option) => option.value === value);
    if (!found) return value;
    return labelByLang(found);
  }

  function moodLabel(value: string): string {
    const found = MOOD_OPTIONS.find((option) => option.value === value);
    if (!found) return value;
    return labelByLang(found);
  }

  function colorToneClass(value: string): string {
    const map: Record<string, string> = {
      Red: 'so-color-red',
      Purple: 'so-color-purple',
      Blue: 'so-color-blue',
      White: 'so-color-white',
      Yellow: 'so-color-yellow',
      Black: 'so-color-black',
      Green: 'so-color-green',
      Pink: 'so-color-pink',
      Orange: 'so-color-orange',
      Brown: 'so-color-brown'
    };
    return map[value] ?? '';
  }

  function moodToneClass(value: string): string {
    const map: Record<string, string> = {
      Bright: 'so-mood-bright',
      Warm: 'so-mood-warm',
      Bouncy: 'so-mood-bouncy',
      Rough: 'so-mood-rough',
      Sensitive: 'so-mood-sensitive',
      Sharp: 'so-mood-sharp',
      HotBlooded: 'so-mood-hotblooded',
      DoubleFaced: 'so-mood-doublefaced',
      Hidden: 'so-mood-hidden',
      Chuunibyou: 'so-mood-chuunibyou',
      Soft: 'so-mood-soft',
      Romantic: 'so-mood-romantic',
      Tsundere: 'so-mood-tsundere',
      Miserly: 'so-mood-miserly',
      Burly: 'so-mood-burly',
      Slender: 'so-mood-slender',
      Intellectual: 'so-mood-intellectual'
    };
    return map[value] ?? '';
  }

  function abvToneClass(value: AbvOption): string {
    if (value === 'Light') return 'so-abv-light';
    if (value === 'Medium') return 'so-abv-medium';
    return 'so-abv-strong';
  }

  function optionClass(prefix: string, value: string): string {
    return `${prefix}-${value.toLowerCase().replace(/\s+/g, '-')}`;
  }

  function toggleColor(option: string) {
    setHint('');
    setColors((prev) => {
      if (prev.includes(option)) return prev.filter((v) => v !== option);
      if (prev.length >= 2) {
        setHint(t.maxColor);
        return prev;
      }
      return [...prev, option];
    });
  }

  function toggleMood(option: string) {
    setHint('');
    setMoods((prev) => {
      if (prev.includes(option)) return prev.filter((v) => v !== option);
      if (prev.length >= 3) {
        setHint(t.maxMood);
        return prev;
      }
      return [...prev, option];
    });
  }

  function validate(): string[] {
    const nextErrors: string[] = [];
    if (colors.length < 1 || colors.length > 2) nextErrors.push(t.errColor);
    if (moods.length < 1 || moods.length > 3) nextErrors.push(t.errMood);
    if (!abv) nextErrors.push(t.errAbv);
    if (keyword.length > 50) nextErrors.push(t.errKeyword);
    return nextErrors;
  }

  function onImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    if (referencePreview) URL.revokeObjectURL(referencePreview);
    const previewUrl = URL.createObjectURL(file);
    setReferenceImage(file);
    setReferencePreview(previewUrl);
  }

  function clearImage() {
    if (referencePreview) URL.revokeObjectURL(referencePreview);
    setReferenceImage(null);
    setReferencePreview(null);
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setHint('');

    const nextErrors = validate();
    setErrors(nextErrors);
    if (nextErrors.length) return;

    const payload: SecretOrderPayload = {
      colors,
      moods,
      abv: abv as AbvOption,
      ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
      ...(referencePreview ? { referenceImagePreview: referencePreview } : {}),
      createdAtKst: kst.createdAtKst
    };

    localStorage.setItem('secretOrderDraft', JSON.stringify(payload));
    console.log('secretOrderDraft', payload);
    // TODO: Reuse existing order delivery integration when endpoint/webhook is finalized.
    setSubmitted(payload);
    setSummaryOpen(true);
  }

  useEffect(() => {
    return () => {
      if (referencePreview) URL.revokeObjectURL(referencePreview);
    };
  }, [referencePreview]);

  return (
    <section className="secretorder-page">
      <Seo title={t.pageTitle} description={t.pageDesc} />

      <article className="card secretorder-shell">
        <header className="secretorder-head">
          <div className="secretorder-head-top">
            <h1>{t.heading}</h1>
            <div className="qr-lang-wrap">
              <button type="button" className="qr-lang-btn" onClick={() => setLangOpen((v) => !v)}>
                {t.language}
              </button>
              {langOpen ? (
                <div className="qr-lang-menu">
                  <button type="button" onClick={() => { setLang('ko'); setLangOpen(false); }} className={lang === 'ko' ? 'active' : ''}>
                    {t.korean}
                  </button>
                  <button type="button" onClick={() => { setLang('en'); setLangOpen(false); }} className={lang === 'en' ? 'active' : ''}>
                    {t.english}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <p>{t.line1}</p>
          {t.line2 ? <p>{t.line2}</p> : null}
          <p>{t.line3}</p>
          <p>{t.line4}</p>
          <p>{t.line5}</p>
        </header>

        <form className="secretorder-form" onSubmit={onSubmit}>
          <fieldset className="secretorder-fieldset">
            <legend>{t.colorLegend}</legend>
            <div className="secretorder-chip-wrap">
              {COLOR_OPTIONS.map((option) => (
                (() => {
                  const selected = colors.includes(option.value);
                  return (
                <button
                  key={option.value}
                  type="button"
                  className={
                    selected
                      ? `secret-chip ${optionClass('secret-color', option.value)} active`
                      : `secret-chip ${optionClass('secret-color', option.value)}`
                  }
                  onClick={() => toggleColor(option.value)}
                  aria-pressed={selected}
                >
                  {selected ? <span className="secret-selected-mark">✓</span> : null}
                  {labelByLang(option)}
                </button>
                  );
                })()
              ))}
            </div>
          </fieldset>

          <fieldset className="secretorder-fieldset">
            <legend>{t.moodLegend}</legend>
            <div className="secretorder-chip-wrap">
              {MOOD_OPTIONS.map((option) => (
                (() => {
                  const selected = moods.includes(option.value);
                  return (
                <button
                  key={option.value}
                  type="button"
                  className={
                    selected
                      ? `secret-chip ${optionClass('secret-mood', option.value)} active`
                      : `secret-chip ${optionClass('secret-mood', option.value)}`
                  }
                  onClick={() => toggleMood(option.value)}
                  aria-pressed={selected}
                >
                  {selected ? <span className="secret-selected-mark">✓</span> : null}
                  {labelByLang(option)}
                </button>
                  );
                })()
              ))}
            </div>
          </fieldset>

          <fieldset className="secretorder-fieldset">
            <legend>{t.abvLegend}</legend>
            <div className="secretorder-segment">
              {ABV_OPTIONS.map((option) => (
                (() => {
                  const selected = abv === option;
                  return (
                <button
                  key={option}
                  type="button"
                  className={
                    selected
                      ? `secret-segment ${optionClass('secret-abv', option)} active`
                      : `secret-segment ${optionClass('secret-abv', option)}`
                  }
                  onClick={() => setAbv(option)}
                  aria-pressed={selected}
                >
                  {selected ? <span className="secret-selected-mark">✓</span> : null}
                  {abvLabel(option)}
                </button>
                  );
                })()
              ))}
            </div>
          </fieldset>

          <fieldset className="secretorder-fieldset">
            <legend>{t.keywordLegend}</legend>
            <input
              className="secretorder-input"
              type="text"
              maxLength={50}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t.keywordPlaceholder}
            />
            <small>{keyword.length}/50</small>
          </fieldset>

          <fieldset className="secretorder-fieldset">
            <legend>{t.imageLegend}</legend>
            <input type="file" accept="image/*" onChange={onImageChange} />
            <small>{t.imageHelp}</small>
            {referencePreview ? (
              <div className="secretorder-image-preview">
                <img src={referencePreview} alt="reference preview" />
                <button type="button" className="btn" onClick={clearImage}>{t.removeImage}</button>
              </div>
            ) : null}
          </fieldset>

          {hint ? <p className="secretorder-hint">{hint}</p> : null}
          {errors.length ? (
            <div className="secretorder-errors">
              {errors.map((msg) => (
                <p key={msg}>{msg}</p>
              ))}
            </div>
          ) : null}

          <button type="submit" className="btn active secretorder-submit">
            {t.order}
          </button>
        </form>
      </article>

      {summaryOpen && submitted ? (
        <aside className="qr-order-modal" role="dialog" aria-modal="true">
          <div className="qr-order-card secretorder-modal-card">
            <button type="button" className="qr-popup-close" onClick={() => setSummaryOpen(false)}>
              {t.close}
            </button>
            <h3>{t.summary}</h3>
            <div className="secretorder-status-row">
              <span className={popupIsAvailable ? 'secretorder-badge on' : 'secretorder-badge off'}>
                {popupIsAvailable ? t.statusOn : t.statusOff}
              </span>
              <small>{t.now}: {kst.displayNow}</small>
            </div>
            {!popupIsAvailable ? <p className="secretorder-closed-note">{t.closedNote}</p> : null}
            <p className="secretorder-summary-inline">
              <strong>{t.fieldColor} / {t.fieldMood} / {t.fieldAbv}:</strong>
              <br />
              <span className="secretorder-summary-vals">
                <span>
                  {submitted.colors.map((value) => (
                    <span key={value} className={colorToneClass(value)}>{colorLabel(value)} </span>
                  ))}
                </span>
                <span> / </span>
                <span>
                  {submitted.moods.map((value) => (
                    <span key={value} className={moodToneClass(value)}>{moodLabel(value)} </span>
                  ))}
                </span>
                <span> / </span>
                <span className={abvToneClass(submitted.abv)}>{abvLabel(submitted.abv)}</span>
              </span>
            </p>
            <p><strong>{t.fieldKeyword}:</strong> {submitted.keyword || t.noKeyword}</p>
            {submitted.referenceImagePreview ? (
              <div className="secretorder-modal-image">
                <img src={submitted.referenceImagePreview} alt="submitted reference" />
              </div>
            ) : (
              <p><strong>{t.fieldImage}:</strong> {t.noImage}</p>
            )}
            <p className="secretorder-show-note">{t.showToStaff}</p>
          </div>
        </aside>
      ) : null}
    </section>
  );
}
